import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { scrollToSection } from "@/lib/smoothScroll";
import { useLang } from "@/context/LanguageContext";

const HERO_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/files/bertagnolli-site/uploads/f4f00407-2aed-4938-bf6f-d91d9bbce230.webp`;

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative h-[100svh] min-h-[560px] overflow-hidden grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt={t("hero.imgAlt")}
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#090D14]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#090D14] via-[#090D14]/45 to-[#090D14]/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#090D14]/75 via-[#090D14]/15 to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-between sm:justify-end pt-32 sm:pt-28 pb-24 sm:pb-28">
        <div>
        <h1 className="font-display leading-[0.9] text-slate-50">
          <MaskedLine delay={0.4} className="text-4xl sm:text-7xl lg:text-8xl xl:text-[8.5rem]">GIACOMO</MaskedLine>
          <MaskedLine delay={0.55} className="text-4xl sm:text-7xl lg:text-8xl xl:text-[8.5rem]">
            BERTAGNOLLI<span className="text-amber-400">.</span>
          </MaskedLine>
        </h1>

        <motion.span
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 inline-flex w-fit items-center gap-2 rounded-full border border-sky-400/40 bg-[#090D14]/50 backdrop-blur-sm px-4 py-1.5 text-[9px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] font-semibold text-sky-300"
        >
          <span className="h-1.5 w-1.5 rotate-45 bg-sky-400" />
          {t("hero.badge")}
        </motion.span>
        </div>

        <div>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 text-base sm:text-lg lg:text-xl text-slate-300 max-w-xl font-light"
        >
          {t("hero.payoff")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-percorso"
            onClick={() => scrollToSection("#percorso")}
            className="group relative overflow-hidden rounded-full bg-amber-400 px-8 py-4 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#090D14] transition-transform duration-300 hover:scale-[1.04]"
          >
            <span className="relative z-10">{t("hero.ctaPercorso")}</span>
            <span className="absolute inset-0 -translate-x-full bg-amber-300 transition-transform duration-500 group-hover:translate-x-0" />
          </button>
          <button
            data-testid="hero-cta-risultati"
            onClick={() => scrollToSection("#risultati")}
            className="rounded-full border border-slate-500/60 px-8 py-4 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-slate-100 backdrop-blur-sm transition-colors duration-300 hover:border-amber-400 hover:text-amber-400"
          >
            {t("hero.ctaRisultati")}
          </button>
          <button
            data-testid="hero-cta-collaborazioni"
            onClick={() => scrollToSection("#oltre-lo-sport")}
            className="group inline-flex items-center gap-2 px-6 sm:px-7 py-3.5 text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em] text-sky-300 hover:text-sky-200 transition-colors duration-300"
          >
            <span className="border-b border-sky-400/40 pb-0.5 group-hover:border-sky-300 transition-colors duration-300">{t("hero.ctaCollab")}</span>
          </button>
        </motion.div>
        </div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#chi-sono")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-slate-400 hover:text-amber-400 transition-colors"
        aria-label={t("hero.scrollAria")}
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} className="block">
          <ChevronDown className="h-7 w-7" />
        </motion.span>
      </motion.button>
    </section>
  );
}
